import { useEffect, useMemo, useState } from 'react';

import { logError } from '@/modules/analytics';
import { fetchLegalDocuments, type LegalDocument } from '@/modules/api';

import { selectSignableDocuments } from '../utils/selectSignableDocuments';

type UseLegalDocuments = {
  documents: LegalDocument[];
  /**
   * Disclaimer paragraphs that the server attaches to specific documents
   * (e.g. the cookies / marketing copy that hangs off the Privacy Policy
   * doc). Rendered as legal copy near the checkboxes — not anchor labels.
   */
  additionalLanguages: string[];
  isLoading: boolean;
  error: unknown;
};

// Fetches the legal-documents catalog on mount and keeps it in local hook
// state (matches Sparta — the catalog is screen-scoped, not shared, so it
// doesn't belong in RulesEngineProvider). The returned `documents` are
// filtered to only those still requiring signature; callers pass their
// `uuid`s in the `signed_documents` array of the createUser payload after the
// user accepts.
export function useLegalDocuments(): UseLegalDocuments {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const additionalLanguages = useMemo(
    () =>
      documents
        .map((d) => d.additionalLanguage)
        .filter((text): text is string => !!text && text.trim().length > 0),
    [documents]
  );

  useEffect(() => {
    let cancelled = false;
    fetchLegalDocuments()
      .then((res) => {
        if (cancelled) return;
        setDocuments(selectSignableDocuments(res));
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // A catalog fetch failure blocks account creation (Continue is gated on
        // it), so log it to Datadog Logs — otherwise the user is silently stuck.
        logError('legal documents load error', {
          error: err instanceof Error ? err.message : String(err),
        });
        setError(err);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { documents, additionalLanguages, isLoading, error };
}
